import { useEffect, useRef, useState } from 'react';
import {
  assetConstants,
  type ChainflipChain,
  readAssetValue,
  type ChainflipAsset,
  type AnyChainflipAsset,
} from '@chainflip/utils/chainflip';
import { isNotNullish, isNullish } from '@chainflip/utils/guard';
import classNames from 'classnames';
import { MacScrollbar } from 'mac-scrollbar';
import { type ChainflipToken } from '@/shared/assets/tokens';
import AddressInput from '@/shared/components/AddressInput';
import { Divider } from '@/shared/components/Divider';
import Button from '@/shared/components/flip-ui-kit/Button';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import useSupportedAssets from '@/shared/hooks/useSupportedAssets';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import CloseIcon from '@/shared/icons/flip-ui-kit/large/Close';
import { chainflipAssetMap } from '@/shared/utils/env';
import { formatUsdValue } from '@/shared/utils/numbers';
import { assetSupportsStrategyType, type AllowedStrategyType } from '@/shared/utils/strategies';
import TokenAmount from '@/shared/utils/TokenAmount';
import useRefundAddresses from '../hooks/useRefundAddresses';
import { useStateChainAccount } from '../hooks/useStateChainAccount';
import useUpdateRefundAddress from '../hooks/useUpdateRefundAddress';

const TooltipContent = () => (
  <div className="flex flex-col gap-y-2">
    <span className="font-aeonikMedium text-14 text-cf-white">Refund address required</span>
    <div className="h-px bg-[#2B2B2B]" />
    <span className="text-12 text-cf-light-3">
      To deposit this asset you must provide a refund address in case we need to return funds to
      you.
    </span>
  </div>
);

const AddRefundAddressButton = () => {
  const [isTooltipEnabled, setIsTooltipEnabled] = useState(false);
  const showTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
    showTimeout.current = setTimeout(() => setIsTooltipEnabled(true), 500);
  };

  const handleMouseLeave = () => {
    if (showTimeout.current) {
      clearTimeout(showTimeout.current);
      showTimeout.current = null;
    }
    hideTimeout.current = setTimeout(() => setIsTooltipEnabled(false), 200);
  };

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Tooltip disabled={!isTooltipEnabled} content={<TooltipContent />}>
        <Button type="secondary-standard" size="small">
          Add refund address
        </Button>
      </Tooltip>
    </div>
  );
};

const AssetRow = ({
  asset,
  showRefundAddressInput = false,
  onClick,
  onCancel,
}: {
  asset: ChainflipAsset;
  showRefundAddressInput?: boolean;
  onClick: (token: ChainflipToken) => void;
  onCancel?: () => void;
}) => {
  const token = chainflipAssetMap[asset];
  const { freeBalances } = useStateChainAccount();
  const { price = 0 } = useTokenPrice(token);
  const refundAddresses = useRefundAddresses();
  const [address, setAddress] = useState(
    refundAddresses?.[token.chain.chainflipId as ChainflipChain] ?? '',
  );
  const [addressInputError, setAddressInputError] = useState<string>();

  const balance = freeBalances?.[token.chainflipId as ChainflipAsset] ?? TokenAmount.ZERO;

  const assetRef = useRef<HTMLDivElement>(null);

  const handleAddressInput = (refundAddress: string) => {
    setAddressInputError(undefined);
    setAddress(refundAddress);
  };

  useEffect(() => {
    if (showRefundAddressInput)
      assetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setAddress('');
    setAddressInputError(undefined);
  }, [showRefundAddressInput]);

  const { mutate: onSave, isPending } = useUpdateRefundAddress({
    chain: token.chain.chainflipId,
    setAddressInputError,
    address,
    onComplete: onCancel,
  });

  const hasRefundAddress = Boolean(refundAddresses?.[token.chain.chainflipId as ChainflipChain]);

  return (
    <div className="flex flex-col gap-y-2" ref={assetRef}>
      <div
        className="flex w-full cursor-pointer items-center justify-between rounded-md p-3 hover:bg-cf-gray-4"
        onClick={() => {
          onClick(token);
        }}
        role="button"
        tabIndex={0}
      >
        <div
          className={classNames(
            'flex items-center gap-x-2',
            !hasRefundAddress && !showRefundAddressInput && 'opacity-50',
          )}
        >
          <TokenWithChainByAsset asset={token.chainflipId} size="large" />
          <div className="flex flex-col text-14">
            <span className="font-aeonikMedium text-cf-white">{token.symbol}</span>
            <span className="text-cf-light-2">on {token.chain.name}</span>
          </div>
        </div>
        {hasRefundAddress ? (
          <div className="flex flex-col text-end font-aeonikMedium text-13">
            <span className="text-cf-white">
              {balance.toCapped()} {token.symbol}
            </span>
            <span className="text-cf-light-2">{formatUsdValue(balance.mul(price).toFixed(2))}</span>
          </div>
        ) : (
          <AddRefundAddressButton />
        )}
      </div>
      {showRefundAddressInput && !hasRefundAddress && (
        <div className="mb-2 flex flex-col gap-y-6 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4">
          <span className="text-14 text-cf-light-2">
            You must provide a refund address for network security. You only need to do this once
            and can be changed anytime.
          </span>
          <Divider className="h-px" />
          <AddressInput
            token={token}
            address={address}
            setAddress={handleAddressInput}
            focusOnMount
            className="border-cf-gray-5 bg-cf-gray-4"
            inputLabelClassName="bg-cf-gray-4"
            error={addressInputError}
            placeholder="Refund Address"
          />
          <div className="flex gap-x-4 self-end">
            <Button
              disabled={isPending}
              type="secondary-standard"
              onClick={() => {
                onCancel?.();
              }}
            >
              Cancel
            </Button>
            <Button loading={isPending && 'iconOnly'} onClick={onSave}>
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const SelectAssetModalContent = ({
  modalHeader = 'Deposit',
  onlyStablecoin,
  selectToken,
  onClose,
  showOnlyAvailableAssets = false,
  strategyType,
}: {
  modalHeader?: string;
  onlyStablecoin?: boolean;
  selectToken: (token: ChainflipToken) => void;
  onClose: () => void;
  showOnlyAvailableAssets?: boolean;
  strategyType?: AllowedStrategyType;
}) => {
  const chainflipAssets = useSupportedAssets();
  const assets = onlyStablecoin
    ? chainflipAssets.filter((asset) => {
        const token = chainflipAssetMap[asset];
        return (
          token.isStablecoin &&
          token.chainflipId !== 'Usdc' &&
          assetSupportsStrategyType(strategyType, asset)
        );
      })
    : chainflipAssets;

  const { account } = useStateChainAccount();
  const refundAddresses = useRefundAddresses();

  const [selectedAssetWithoutRefundAddress, setSelectedAssetWithoutRefundAddress] =
    useState<AnyChainflipAsset>();

  const onRowClick = (token: ChainflipToken) =>
    setSelectedAssetWithoutRefundAddress((prev) =>
      prev === token.chainflipId ? undefined : token.chainflipId,
    );

  const onCancel = () => setSelectedAssetWithoutRefundAddress(undefined);

  const assetsWithRefundAddress = assets.filter((asset) =>
    isNotNullish(refundAddresses?.[assetConstants[asset].chain as ChainflipChain]),
  );

  const assetsWithoutRefundAddress = assets.filter((asset) =>
    isNullish(refundAddresses?.[assetConstants[asset].chain as ChainflipChain]),
  );

  const assetsWithBalance = chainflipAssets.filter(
    (asset) => account?.asset_balances && readAssetValue(account.asset_balances, asset) > 0n,
  );

  return (
    <div className="flex max-h-[640px] w-[497px] flex-col gap-y-4 rounded-[6px] border border-cf-gray-4 bg-cf-gray-3 p-4">
      <div className="flex justify-between font-aeonikMedium text-16 text-cf-light-3">
        {modalHeader}
        <button type="button" onClick={onClose}>
          <CloseIcon />
        </button>
      </div>
      <hr className="border-cf-gray-4" />
      <span className="font-aeonikMedium text-13 text-cf-light-2">Balances</span>
      <MacScrollbar
        skin="dark"
        // create some space between the scrollbar and the content
        className="-mr-1 pr-1"
      >
        <div className="flex flex-col">
          {showOnlyAvailableAssets ? (
            assetsWithBalance.map((asset) => (
              <AssetRow key={asset} asset={asset} onClick={selectToken} />
            ))
          ) : (
            <>
              {assetsWithRefundAddress.map((asset) => (
                <AssetRow key={asset} asset={asset} onClick={selectToken} />
              ))}
              {assetsWithoutRefundAddress.length > 0 && <hr className="my-2 border-cf-gray-4" />}
              {assetsWithoutRefundAddress.map((asset) => (
                <AssetRow
                  key={asset}
                  asset={asset}
                  showRefundAddressInput={selectedAssetWithoutRefundAddress === asset}
                  onClick={onRowClick}
                  onCancel={onCancel}
                />
              ))}
            </>
          )}
        </div>
      </MacScrollbar>
    </div>
  );
};
